function stripCmsDisplayTitle(title) {
  if (!title) return "";
  let result = title;
  if (result.includes("|")) {
    result = result.split("|").pop().trim();
  }
  if (result.includes("[") && result.includes("]")) {
    result = result.split("]").pop().trim();
  }
  return result;
}

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.inputName = this.input?.getAttribute("name") || "q";
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.isOpen = false;

    // clear event attached
    this.clearBtn = this.querySelector('.search-clear-btn');

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector("form.search");
    this.form = form;

    if (!form) {
      return;
    }

    form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.select = this.querySelector("select");

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );

    if (this.select) {
      this.select.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this),
      );
    }

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));

    /* CLEAR BUTTON LOGIC */
    if (this.clearBtn) {
      this.clearBtn.addEventListener("click", () => {
        this.input.value = "";
        this.syncSearchMode("");
        this.clearBtn.style.display = "none";
        this.close(true);
        this.input.focus();
      });
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  getSearchFilterType() {
    if (!this.select) {
      return "";
    }

    return this.select.dataset.type;
  }

  getSearchFilterTypeQuery() {
    if (!this.select) {
      return "";
    }

    return this.select.value.trim();
  }

  getNormalizedQuery(query = this.input.value) {
    return query.trim().replace(/\s+/g, " ");
  }

  looksLikeProductIdentifier(token) {
    return /[0-9]/.test(token) || /[-_.]/.test(token) || (token === token.toUpperCase() && /[A-Z]/.test(token));
  }

  getSearchTokens(query = this.getQuery()) {
    const normalizedQuery = this.getNormalizedQuery(query);

    if (!normalizedQuery) {
      return [];
    }

    const tokens = normalizedQuery.split(/[,\|\s]+/).map((token) => token.trim()).filter(Boolean);

    if (/[,\|]/.test(normalizedQuery)) {
      return tokens;
    }

    const whitespaceSeparatedProductCodes =
      tokens.length > 1 &&
      tokens.every((token) => this.looksLikeProductIdentifier(token)) &&
      tokens.some((token) => /[0-9]|[-_.]/.test(token));

    if (whitespaceSeparatedProductCodes) {
      return tokens;
    }

    return [normalizedQuery];
  }

  isMultiProductSearch(query = this.getQuery()) {
    return this.getSearchTokens(query).length > 1;
  }

  escapeSearchQueryValue(value) {
    const escapedValue = value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
    return /[^A-Za-z0-9._-]/.test(escapedValue) ? `"${escapedValue}"` : escapedValue;
  }

  buildMultiProductQuery(query = this.getQuery()) {
    const tokens = this.getSearchTokens(query);
    const filterType = this.getSearchFilterType();
    const filterTypeQuery = this.getSearchFilterTypeQuery();
    const tokensQuery = `(${tokens
      .map((token) => this.escapeSearchQueryValue(token))
      .join(" OR ")})`;

    if (!filterType || !filterTypeQuery) {
      return tokensQuery;
    }

    return `${filterType}:${this.escapeSearchQueryValue(filterTypeQuery)} AND ${tokensQuery}`;
  }

  setFormHiddenInput(name, value) {
    if (!this.form) {
      return;
    }

    let hiddenInput = this.form.querySelector(`[data-search-hidden-input="${name}"]`);

    if (!value) {
      if (hiddenInput) {
        hiddenInput.remove();
      }
      return;
    }

    if (!hiddenInput) {
      hiddenInput = document.createElement("input");
      hiddenInput.type = "hidden";
      hiddenInput.name = name;
      hiddenInput.dataset.searchHiddenInput = name;
      this.form.appendChild(hiddenInput);
    }

    hiddenInput.value = value;
  }

  syncSearchMode(query = this.getQuery()) {
    const isMultiProductSearch = this.isMultiProductSearch(query);
    this.setFormHiddenInput("type", isMultiProductSearch ? "product" : "");
    this.setFormHiddenInput("display_q", isMultiProductSearch ? query : "");
    this.restoreVisibleSearchInput();
    this.setFormHiddenInput(this.inputName, "");

    return isMultiProductSearch;
  }

  restoreVisibleSearchInput() {
    if (!this.input || this.input.getAttribute("name") === this.inputName) {
      return;
    }

    this.input.setAttribute("name", this.inputName);
  }

  prepareMultiProductSubmit(query) {
    this.setFormHiddenInput(this.inputName, this.buildMultiProductQuery(query));
    this.input.removeAttribute("name");
  }

  showDefaultResults() {
    const defaultMarkup = document.querySelector('#predictive-search-default-template');
    if (!defaultMarkup) {
      return;
    }
    this.predictiveSearchResults.innerHTML = defaultMarkup.innerHTML;
    this.setAttribute("results", true);
    this.open();
  }

  onChange() {
    const searchTerm = this.getQuery();
    const isMultiProductSearch = this.syncSearchMode(searchTerm);

    if (this.clearBtn) {
      this.clearBtn.style.display = searchTerm.length ? "block" : "none";
    }

    if (!searchTerm.length) {
      this.showDefaultResults();
      return;
    }

    this.getSearchResults(searchTerm);

    if (!isMultiProductSearch) {
      this.fetchPageContentResults(searchTerm);
    }
  }

  onFormSubmit(event) {
    const searchTerm = this.getQuery();

    if (!searchTerm.length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }

    if (this.syncSearchMode(searchTerm)) {
      this.prepareMultiProductSubmit(searchTerm);
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.showDefaultResults();
      return;
    }

    if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) {
      return;
    }

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search-item-button"),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button',
    );

    if (selectedProduct) {
      selectedProduct.click();
    }
  }

  getSearchResults(searchTerm) {
    const filterType = this.getSearchFilterType();
    const filterTypeQuery = this.getSearchFilterTypeQuery();
    const isMultiProductSearch = this.isMultiProductSearch(searchTerm);
    const queryValue = isMultiProductSearch ? this.buildMultiProductQuery(searchTerm) : searchTerm;

    // Strip trailing separators (e.g. "824830," → "824830") for single-term searches
    const cleanedSearchTerm = isMultiProductSearch
      ? searchTerm
      : (this.getSearchTokens(searchTerm)[0] || searchTerm);
    const queryKey =
      queryValue.replaceAll(" ", "-").toLowerCase() +
      "-" +
      filterType +
      "-" +
      filterTypeQuery.replaceAll(" ", "-").toLowerCase() +
      "-" +
      (isMultiProductSearch ? "product-only" : "all-resources");

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      const cached = this.cachedResults[queryKey];
      this.renderSearchResults(cached.markup, cached.products, cached.term);
      return;
    }

    const limit = this.dataset.limit;
    const limitScope = this.dataset.limitScope;
    const query = isMultiProductSearch
      ? encodeURIComponent(queryValue)
      : filterTypeQuery
      ? encodeURIComponent(`${filterType}:${filterTypeQuery}+* ${cleanedSearchTerm}`)
      : encodeURIComponent(cleanedSearchTerm);
    const resourceTypeQuery = isMultiProductSearch ? "&resources[type]=product" : "";

    const predictiveFetch = fetch(
      `${routes.predictive_search_url}?q=${query}${resourceTypeQuery}&resources[limit_scope]=${limitScope}&resources[limit]=${limit}&options[prefix]=last&section_id=predictive-search`,
    ).then((response) => {
      if (!response.ok) throw new Error(response.status);
      return response.text();
    });

    // Fetch products from the standard search engine (same as the search results page).
    // For comma-separated SKUs, use the OR query (e.g. "SKU1" OR "SKU2") so all matched products are returned.
    const standardSearchQuery = isMultiProductSearch ? queryValue : cleanedSearchTerm;
    const standardProductsFetch = fetch(
      `/search?q=${encodeURIComponent(standardSearchQuery)}&type=product&options[prefix]=last&view=predictive-products`,
    )
      .then((res) => (res.ok ? res.json() : null))
      .catch(() => null);

    Promise.all([predictiveFetch, standardProductsFetch])
      .then(([text, standardProducts]) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.cachedResults[queryKey] = {
          markup: resultsMarkup,
          products: standardProducts,
          term: cleanedSearchTerm,
        };
        this.renderSearchResults(resultsMarkup, standardProducts, cleanedSearchTerm);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  // Escapes regex special characters so the search term can be safely used inside a RegExp.
  escapeRegExp(str) {
    return String(str).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  // Case-insensitively wraps the first occurrence(s) of `term` inside `text` in <strong> tags.
  // Text is HTML-escaped first so highlighting never introduces unescaped markup.
  highlightMatch(text, term) {
    const safeText = this.escapeHtml(text || "");
    const trimmedTerm = (term || "").trim();

    if (!trimmedTerm) {
      return safeText;
    }

    const safeTerm = this.escapeRegExp(this.escapeHtml(trimmedTerm));
    const regex = new RegExp(`(${safeTerm})`, "gi");

    return safeText.replace(regex, "<strong>$1</strong>");
  }

  replaceProductsWithStandardSearch(products, searchTerm = "") {
    if (!products || !products.length) return;

    let productList = this.querySelector("#predictive-search-results-item-product-list");

    // If predictive API returned no products, create the section
    if (!productList) {
      const groupWrapper = this.querySelector(".predictive-search-results-group-wrapper");
      if (!groupWrapper) return;
      const group = document.createElement("div");
      group.className = "predictive-search-results-group";
      group.innerHTML = `
        <div class="predictive-search-results-group-section">
          <h3 id="predictive-search-products" class="predictive-search-heading h5">Products</h3>
          <ul id="predictive-search-results-item-product-list" class="predictive-search-results-item-list"></ul>
        </div>`;
      groupWrapper.prepend(group);
      productList = group.querySelector("#predictive-search-results-item-product-list");
    }

    productList.innerHTML = products
      .map((product, index) => {
        const aspectRatio = product.aspect_ratio || 1;
        const imageHtml = product.image
          ? `<div class="aspect-ratio" style="--ratio-percent: ${((1 / aspectRatio) * 100).toFixed(2)}%;">
               <img class="predictive-search-item-image" src="${product.image}" width="140" height="${Math.ceil(140 / aspectRatio)}" loading="lazy" alt="${this.escapeHtml(product.image_alt || "")}">
             </div>`
          : "";
        return `<li
          id="predictive-search-option-product-${index + 1}"
          class="predictive-search-list-item predictive-search-list-item-product"
          role="option"
          aria-selected="false"
        >
          <a href="${product.url}" class="predictive-search-item predictive-search-item-link" tabindex="-1">
            <div class="predictive-search-item-image-wrap">${imageHtml}</div>
            <div class="predictive-search-item-content">
              ${product.vendor ? `<div class="predictive-search-item-vendor">${this.escapeHtml(product.vendor)}</div>` : ""}
              <h3 class="predictive-search-item-heading typography-body">${this.highlightMatch(product.title, searchTerm)}</h3>
              <div class="price"><span class="price-item price-item--regular">${product.price}</span></div>
            </div>
          </a>
        </li>`;
      })
      .join("");
  }

  renderSearchResults(resultsMarkup, standardProducts = null, searchTerm = "") {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    if (standardProducts && standardProducts.length) {
      // standardProducts comes from the unbounded /search endpoint, so it
      // ignores the configured product limit unless we slice it here before render.
      const limit = parseInt(this.dataset.limit, 10) || standardProducts.length;
      this.replaceProductsWithStandardSearch(standardProducts.slice(0, limit), searchTerm);
    }

    this.setLiveRegionResults();
    this.open();
  }

  fetchPageContentResults(query) {
    if (!query || query.length < 3 || this.isMultiProductSearch(query)) return;

    fetch(`/search?q=${encodeURIComponent(query)}&type=page&view=page-content`)
      .then((res) => res.json())
      .then((data) => {

        const visiblePages = data.pages.filter(
          (page) => !page.title || !page.title.includes('[AI FAQ]')
        );

        if (!visiblePages.length) return;

        let container = this.querySelector('#predictive-search-results-item-article-list');

        // Create Pages section if it doesn't exist
        if (!container) {

          const wrapper = this.querySelector('.predictive-search-results-group');

          if (!wrapper) return;

          const section = document.createElement('div');
          section.className = 'predictive-search-results-group-section';

          section.innerHTML = `
            <h3 class="predictive-search-heading h5">Pages</h3>
            <ul id="predictive-search-results-item-article-list" class="predictive-search-results-item-list"></ul>
          `;

          wrapper.appendChild(section);

          container = section.querySelector('ul');
        }

        visiblePages.forEach((page) => {

          const normalizedPageUrl = page.url.split('?')[0];

          // Check if page already exists in predictive results
          const existingLinks = Array.from(
            this.querySelectorAll('.predictive-search-list-item-page a')
          );

          const duplicate = existingLinks.some(link => {
            const existingUrl = link.getAttribute('href').split('?')[0];
            return existingUrl === normalizedPageUrl;
          });

          if (duplicate) return;

          const item = `
            <li class="predictive-search-list-item predictive-search-list-item-page">
              <a href="${page.url}" class="predictive-search-item predictive-search-item-link">
                <div class="predictive-search-item-content">
                  <h3 class="predictive-search-item-heading typography-body">
                    ${stripCmsDisplayTitle(page.title)}
                  </h3>
                  <p style="font-size:12px;opacity:.7;margin-top:4px;">
                    ${page.content}
                  </p>
                </div>
              </a>
            </li>
          `;

          container.insertAdjacentHTML("beforeend", item);

        });

      })
      .catch((err) => console.error(err));
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]").textContent,
    );
  }

  getResultsMaxHeight() {
    const header = document.querySelector(".section-header");
    const headerBottom = header ? header.getBoundingClientRect().bottom : 0;

    const isMobile = window.innerWidth <= 749;

    if (isMobile) {
      const dock = document.getElementById('mobile-dock');
      const dockHeight = dock ? dock.offsetHeight : 0;
      this.resultsMaxHeight = window.innerHeight - headerBottom - dockHeight;
      return this.resultsMaxHeight;
    }

    this.resultsMaxHeight = window.innerHeight - headerBottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;

    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);

    if (!this.isOpen) {
      const event = new CustomEvent("search:open");
      document.dispatchEvent(event);
    }

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) {
      selected.setAttribute("aria-selected", false);
    }

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");

    if (this.isOpen) {
      const event = new CustomEvent("search:close");
      document.dispatchEvent(event);
    }

    this.isOpen = false;
  }
}

customElements.define("predictive-search", PredictiveSearch);